import type { CrossFlowExtensions } from '@atlassiansox/cross-flow-base-types/CrossFlowExtensions';
import type { TargetType } from '@atlassiansox/cross-flow-base-types/targets';
import type { JSONValue } from '@atlassiansox/cross-flow-base-types/types';

import type {
	CrossFlowInteractedUIActionSubjectIDs,
	CrossFlowViewedScreenNames,
	CrossFlowViewedUIActionSubjectIDs,
} from '@atlassian/surface-analytics-specs';

import type { API as APIv0 } from './api/v0';
import type { API } from './api/v1';
export type ExperimentalOptions = Record<string, JSONValue>;

export const Journeys = {
	GET_STARTED: 'get-started',
	DECIDE: 'decide',
	DISCOVER: 'discover',
} as const;

export type JourneyType = (typeof Journeys)[keyof typeof Journeys];

export type AnalyticsAttributes = Record<string, unknown>;

export type JourneyTargetProductDefinition =
	| {
			journey: typeof Journeys.GET_STARTED | typeof Journeys.DECIDE;
			targetProduct: TargetType;
	  }
	| {
			journey?: typeof Journeys.DISCOVER;
	  };

export type Options = JourneyTargetProductDefinition & {
	experimentalOptions?: ExperimentalOptions;
	extensions?: CrossFlowExtensions;
	extraAnalyticsAttributes?: AnalyticsAttributes;
	zIndex?: number;
};

export type OptionsV0 = Options & {
	sourceComponent: string;
	sourceContext: string;
};

export type OptionsV1 = Options & {
	sourceComponent: CrossFlowViewedUIActionSubjectIDs | CrossFlowViewedScreenNames;
	sourceContext: CrossFlowInteractedUIActionSubjectIDs;
	v1: boolean;
};

export interface CompletionStatus {
	success?: boolean;
}

export type OnOpen = (options: OptionsV0 | OptionsV1) => Promise<CompletionStatus>;

export const Reasons = {
	NO_API_SUPPORT: 'Could not provide requested API version',
	NO_PROVIDER: 'Provider not found',
} as const;

export type ReasonType = (typeof Reasons)[keyof typeof Reasons]; // equivalent of `typeof Reason.NO_API_SUPPORT | typeof Reason.NO_PROVIDER | ...`

export interface ContextDisabled {
	isEnabled: false;
	reason: ReasonType;
}

export interface ContextEnabled<T, U> {
	/**
	 * @deprecated Use `apiV1` instead.
	 * This API will be removed in a future version.
	 */
	api: T;

	/**
	 * The API for the latest version of CrossFlow.
	 * Use this for new implementations.
	 * @see http://go/ucia, and https://hello.atlassian.net/wiki/spaces/DISC/pages/3049492075/HOW-TO+Instrument+funnel+events+for+a+Cross+Flow+Touchpoint
	 */
	apiV1: U;

	isEnabled: true;
}

export type CrossFlowContextType = ContextEnabled<APIv0, API> | ContextDisabled;

export type NegotiateCrossFlowAPI = (version: number) => CrossFlowContextType;

export interface WithCrossFlowProps {
	crossFlow: CrossFlowContextType;
}
